import React from 'react';
import { Facebook, Instagram, Linkedin } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLaunch } from '../contexts/LaunchContext';
import { COMPANY, SUPPORT_EMAIL } from '../data/launch';
const LINK_CLASS = 'text-gray-400 hover:text-white transition-colors duration-150 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow';
const socials = [{
  href: 'https://www.linkedin.com/company/kitty-pot/',
  label: 'KittyPot on LinkedIn',
  Icon: Linkedin
}, {
  href: 'https://www.facebook.com/people/Kittypotapp/100089385062763/',
  label: 'KittyPot on Facebook',
  Icon: Facebook
}, {
  href: 'https://www.instagram.com/kittypotapp',
  label: 'KittyPot on Instagram',
  Icon: Instagram
}];
const legalLinks = [{
  to: '/privacy-policy',
  label: 'Privacy Policy'
}, {
  to: '/delete-account',
  label: 'Delete account'
}, {
  to: '/terms',
  label: 'Terms of Use'
}, {
  to: '/cookie-policy',
  label: 'Cookie Policy'
}, {
  to: '/disclaimer',
  label: 'Disclaimer'
}, {
  to: '/contact',
  label: 'Contact'
}];
export function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isPublicLaunch
  } = useLaunch();
  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <footer className="bg-gray-950 pt-16 pb-8 border-t-2 border-kitty-yellow" data-id="element-116" data-mp="hsttig7oqe35">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117" data-mp="huwgsshgdt46">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-118" data-mp="cv1ruuh8ndlr">
          <div className="col-span-1 md:col-span-2" data-id="element-119" data-mp="qt03hs46cvxk">
            <Link to="/" className="inline-flex items-center gap-2 mb-4 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-120" data-mp="qtrs2izcttec">
              <span className="bg-white rounded-lg w-10 h-10 flex items-center justify-center" data-id="element-121" data-mp="twxsjd47hgaw">
                <img src="https://cdn.magicpatterns.com/uploads/vpaWAD2nLEfW1hoAd36jz7/LPot_only.png" alt="" className="w-[26px] h-[26px] object-contain" data-id="element-122" data-mp="yuh8qikgs35h" />
              </span>
              <span className="font-bold text-xl tracking-tight text-white" data-id="element-123" data-mp="1cm44mhahv9nq">
                KittyPot
              </span>
            </Link>
            <p className="text-gray-300 max-w-sm mb-6" data-id="element-124" data-mp="1hj4aar3fmaeb">
              KittyPot helps groups keep track of shared expenses and works out
              who owes whom.
            </p>
            <div className="flex gap-3" data-id="element-125" data-mp="1rhtwgw2d2h0d">
              {socials.map(({
              href,
              label,
              Icon
            }) => <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-11 h-11 rounded-full bg-white/[0.06] flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/[0.12] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-126" data-mp="3oc6qmwwjaxyz">
                  <Icon className="w-5 h-5" aria-hidden="true" data-id="element-127" data-mp="1wnz05822pbis" />
                </a>)}
            </div>
          </div>

          <div data-id="element-128" data-mp="2tcoi0ydpa44r">
            <h2 className="font-bold text-kitty-yellow mb-4" data-id="element-129" data-mp="2tdg4njpbkriw">Product</h2>
            <ul className="space-y-3" data-id="element-130" data-mp="2pdfo9tinu697">
              <li data-id="element-131" data-mp="2pa9x88qh0uau">
                <button onClick={() => scrollToSection('how-it-works')} className={`${LINK_CLASS} text-left`} data-id="element-132" data-mp="2kdb49tijmte8">
                  How it works
                </button>
              </li>
              <li data-id="element-133" data-mp="18z5ycuyrs9g9">
                <button onClick={() => scrollToSection('features')} className={`${LINK_CLASS} text-left`} data-id="element-134" data-mp="18zph2dw8v1vc">
                  Features
                </button>
              </li>
              <li data-id="element-135" data-mp="2rtwkvw8dco95">
                <Link to="/faq" className={LINK_CLASS} data-id="element-136" data-mp="2wm7nx7yeoq5x">
                  FAQ
                </Link>
              </li>
              <li data-id="element-137" data-mp="2ivufl4hlr9g2">
                <Link to="/about" className={LINK_CLASS} data-id="element-138" data-mp="2iwclytxzb41p">
                  About us
                </Link>
              </li>
              {!isPublicLaunch && <li data-id="element-139" data-mp="3bj725enrjh9d">
                  <button onClick={() => scrollToSection('get-started')} className={`${LINK_CLASS} text-left`} data-id="element-140" data-mp="3bkrwp4a2v078">
                    Join the waitlist
                  </button>
                </li>}
            </ul>
          </div>

          <div data-id="element-141" data-mp="36jca37s94lmq">
            <h2 className="font-bold text-kitty-yellow mb-4" data-id="element-142" data-mp="36j2lnomuumj5">Legal</h2>
            <ul className="space-y-3" data-id="element-143" data-mp="1q5ccw4ksja36">
              {legalLinks.map(link => <li key={link.to} data-id="element-144" data-mp="1v6034o173mwc">
                  <Link to={link.to} className={LINK_CLASS} data-id="element-145" data-mp="1v71r1t3b83vh">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/[0.08] flex flex-col items-center gap-4 text-center" data-id="element-146" data-mp="1imcua155lmkv">
          <p className="text-gray-300 text-sm max-w-2xl" data-id="element-147" data-mp="1wluoiw17xs1m">
            KittyPot tracks shared expenses and settlement records.
          </p>
          <p className="text-sm max-w-2xl" data-id="element-148" data-mp="13xp3e8696119">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-300 underline underline-offset-2 hover:text-white transition-colors duration-150 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-149" data-mp="13yrtwezny1gn">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="text-gray-400 text-xs max-w-2xl leading-relaxed" data-id="element-150" data-mp="18v8g33nay5r3">
            {COMPANY.legalName} · Company No. {COMPANY.registrationNumber} ·
            Registered in England &amp; Wales
            <br data-id="element-151" data-mp="g6b7js8w7nbj" />
            Registered Office: {COMPANY.addressOneLine}
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 w-full mt-4" data-id="element-152" data-mp="2gadj4grl3jvm">
            <p className="text-gray-400 text-sm" data-id="element-153" data-mp="2l7nf6f9hatsf">
              © {COMPANY.copyrightYear} {COMPANY.legalName}. All rights
              reserved.
            </p>
            <p className="text-gray-400 text-sm" data-id="element-154" data-mp="2uyi11iitbrdc">Built in Cardiff, Wales</p>
          </div>
        </div>
      </div>
    </footer>;
}